export enum FileReturnTypeEnum {
  File = 'file',
  Text = 'text',
  Image = 'image',
  ImageUrl = 'image-url',
  JSON = 'json',
}

export interface HTMLImage extends HTMLImageElement {
  name: string;
  size: number;
  type: string;
}

export type FileReturnType = (File | HTMLImage | string) & { _id?: string };

export type FileValidationResult = {
  isValid: boolean;
  error?: string;
};

export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes

export function validateFiles(
  files: File | File[],
  maxSize: number = MAX_FILE_SIZE
): FileValidationResult {
  const filesArray = Array.isArray(files) ? files : [files];

  for (const file of filesArray) {
    // Validate file size
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File size exceeds 25MB limit. Please upload a smaller file.',
      };
    }
  }

  return { isValid: true };
}

export default function fileReaderResolver(
  file: File,
  type: FileReturnTypeEnum
): Promise<FileReturnType> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      switch (type) {
        case 'image': {
          const img = new Image() as HTMLImage;

          img.src = fileReader.result as string;
          img.name = file.name;
          img.size = file.size;
          img.type = file.type;
          img.onload = () => {
            resolve(img);
          };

          img.onerror = () => {
            reject(new Error('Failed to load image'));
          };

          return;
        }

        case 'json':
        case 'text':
        case 'image-url':
        default:
          if (!fileReader.result) {
            reject(new Error('File Empty'));

            return;
          }

          resolve(fileReader.result.toString());
      }
    };

    fileReader.onabort = reject;
    fileReader.onerror = reject;

    if (['text', 'json'].includes(type)) {
      fileReader.readAsText(file);
    } else {
      fileReader.readAsDataURL(file);
    }
  });
}
